import { CSSObject } from '@emotion/react';
import { DigitalClockSectionPadding, getSizeValue, useHoneycombTheme } from '../../../theming';

export interface DigitalClockStylesParams {
    sectionPadding: DigitalClockSectionPadding;
}

type StyleClasses = 'root';

export function useStyles({ sectionPadding }: DigitalClockStylesParams): {
    styles: { [key in StyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();
    const { palette, components } = theme;
    const componentTheme = components.themes.DigitalClock;
    const { button: buttonTheme, section: sectionTheme } = componentTheme;

    const typographyProps = theme.fn.getTypographyVariant(buttonTheme.typographyVariant);
    const buttonColors = theme.fn.resolveFlatColorObject(buttonTheme.colors);

    const root: CSSObject = {
        '--digital-clock-button-height': getSizeValue(buttonTheme.height),
        '--digital-clock-button-width': getSizeValue(buttonTheme.width),
        '--digital-clock-button-spacing': getSizeValue(sectionTheme.itemSpacing),
        '--digital-clock-section-padding-x': getSizeValue(sectionTheme.paddingX[sectionPadding]),
        '--digital-clock-section-padding-y': getSizeValue(sectionTheme.paddingY),

        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
        maxHeight: '100%',

        // Section styles
        '> [data-digital-clock-section="true"]': {
            width: 'calc(var(--digital-clock-button-width) + var(--digital-clock-section-padding-x) * 2)',

            overflow: 'hidden',
            scrollBehavior: 'auto',
            '&[data-mounted="true"]': {
                scrollBehavior: 'smooth',
            },

            overflowY: 'auto',

            '&:not(:first-of-type)': {
                borderLeft: `1px solid ${palette.divider}`,
            },

            // A pseudo element which adds additional height below the list. This allows items at the bottom of the list
            // to be scrolled to the top of the container when they are selected.
            '&::after': {
                display: 'block',
                content: '""',
                // We want to leave the last option visible when the container is scrolled to the bottom
                height: 'calc(100% - var(--digital-clock-button-height) - (var(--digital-clock-section-padding-y) * 2))',
            },

            // Heading styles
            '[data-digital-clock-section-heading]': {
                position: 'sticky',
                top: 0,
                width: '100%',
                background: palette.background.content,
                zIndex: 1,
                padding: 'var(--digital-clock-section-padding-y) var(--digital-clock-section-padding-x) 4px',
                '> div': {
                    width: getSizeValue(buttonTheme.width),
                },
            },

            // List styles
            '> ul': {
                position: 'relative', // required for scroll offsets to work
                padding: 'var(--digital-clock-section-padding-y) var(--digital-clock-section-padding-x)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--digital-clock-button-spacing)',

                button: {
                    width: getSizeValue(buttonTheme.width),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: getSizeValue(buttonTheme.height),
                    borderRadius: getSizeValue(buttonTheme.radius),
                    fontWeight: typographyProps.fontWeight.semiBold,
                    fontSize: typographyProps.fontSize,

                    backgroundColor: buttonColors.background,
                    color: buttonColors.text,
                    '&:not([disabled])': theme.fn.hoverStyles({ backgroundColor: buttonColors.backgroundHover }),

                    '&[aria-selected="true"]': {
                        backgroundColor: buttonColors.backgroundActive,
                        color: buttonColors.textActive,
                        '&:not([disabled])': theme.fn.hoverStyles({
                            backgroundColor: buttonColors.backgroundActiveHover,
                        }),
                    },
                    ...theme.fn.focusStyles(),
                },
            },
        },
    };

    return {
        styles: {
            root,
        },
    };
}
